// /opt/esni/frontend/src/api.ts
// CORRECTED VERSION - Fixes all type issues and API handling

// ---------- Types that pages import ----------
export type Audience = {
  id: string;
  name: string;
  description?: string | null;
  criteria: any;
};

export type Policy = {
  id?: string | null;
  name: string;
  priority: number;
  enabled: boolean;
  audience_ids: string[];
  components: Array<{
    type: "TRIGGER" | "ACTION" | "CONSTRAINT" | string;
    kind: string;
    config: any;
  }>;
};

export type EventRow = {
  id: string;
  title: string;
  channel: string;
  starts_at: string;
  ends_at: string;
  audience_ids: string[];
  status: string;
};

// ---------- Types used by the EPG page ----------
export type EpgItemDto = {
  id: string;
  title: string;
  starts_at: string;
  ends_at: string;
  status: string;
  audience: string;   // <- UI wants single audience string
  action: string;     // <- UI wants explicit action
  channel: string;    // <- UI wants channel on the item
  kind: string;
  tooltip?: string;   // <- Optional tooltip for better UX
};

export type Row = {
  channel_id: string;
  channel_name: string;
  items: EpgItemDto[];
};

// ---------- Raw shapes from backend (/api/epg) ----------
type EpgItemRaw = {
  id: string;
  title: string;          // e.g., "urn:scte:224:action:blackout for comcast" or "CNN_Main_HLS for all"
  starts_at: string;
  ends_at: string;
  status: string;
  audience_ids: string[]; // e.g., ["comcast"] or ["all"]
  kind: string;           // "blackout" | "program" | ...
};

type EpgRowRaw = {
  channel_id: string;
  channel_name: string;
  items: EpgItemRaw[];
};

// ---------- Component Catalog Types ----------
export type CatalogItem = {
  kind: string;
  default_config: any;
};

export type PolicyComponent = {
  type: "TRIGGER" | "ACTION" | "CONSTRAINT";
  kind: string;
  config: any;
};

// ---------- Minimal axios-like wrapper built on fetch ----------
const BASE = "/api";

// ---------- Auth token storage ----------
const TOKEN_KEY = "esni_token";
export function getToken(): string | null {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
}
export function setToken(t: string): void {
  try { localStorage.setItem(TOKEN_KEY, t); } catch { /* ignore */ }
}
export function clearToken(): void {
  try { localStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ }
}

async function request<T>(
  method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE",
  path: string,
  body?: any,
  params?: Record<string, any>
): Promise<{ data: T }> {
  let url = `${BASE}${path}`;
  
  // Handle query parameters for GET requests
  if (params && Object.keys(params).length > 0) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, String(value));
      }
    });
    const queryString = searchParams.toString();
    if (queryString) {
      url += `?${queryString}`;
    }
  }
  
  const headers: Record<string, string> = {};
  if (body) headers["Content-Type"] = "application/json";
  const token = getToken();
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const options: RequestInit = {
    method,
    headers,
    credentials: "include",
  };
  if (body) options.body = JSON.stringify(body);

  const res = await fetch(url, options);

  // A 401 on anything other than the login call itself means our token is
  // missing or expired - drop it and bounce to the login screen.
  if (res.status === 401 && !path.startsWith("/auth/login")) {
    clearToken();
    if (typeof window !== "undefined" && window.location.pathname !== "/login") {
      window.location.assign("/login");
    }
  }

  if (!res.ok) {
    // Try to surface JSON error bodies nicely
    let msg = `${res.status} ${res.statusText}`;
    try {
      const txt = await res.text();
      if (txt) {
        try {
          const json = JSON.parse(txt);
          msg = json.error || json.message || txt;
        } catch {
          msg = txt;
        }
      }
    } catch {}
    throw new Error(msg);
  }
  
  const data = (res.status === 204 ? (undefined as unknown as T) : ((await res.json()) as T));
  return { data };
}

// Fixed API object with proper params support
export const api = {
  get: <T>(path: string, options?: { params?: Record<string, any> }) => 
    request<T>("GET", path, undefined, options?.params),
  post: <T>(path: string, body?: any) => 
    request<T>("POST", path, body),
  put: <T>(path: string, body?: any) =>
    request<T>("PUT", path, body),
  patch: <T>(path: string, body?: any) => 
    request<T>("PATCH", path, body),
  delete: <T>(path: string, options?: { params?: Record<string, any> }) =>
    request<T>("DELETE", path, undefined, options?.params),
};

export default api;

// ---------- Higher-level helpers (pages import these by name) ----------

/**
 * getEpg: Returns Row[] exactly as EPG.tsx expects (with EpgItemDto items).
 * It adapts backend titles like "ACTION for AUDIENCE" and audience_ids[].
 */
export async function getEpg(from: string, to: string): Promise<Row[]> {
  try {
    const { data } = await api.get<EpgRowRaw[]>("/epg", { params: { from, to } });
    
    // Transform the raw data into the format the UI expects
    return data.map(row => ({
      channel_id: row.channel_id,
      channel_name: row.channel_name,
      items: row.items.map(item => {
        // Backend now returns first-class `action`, `audience`, and `channel` fields.
        // Prefer them; fall back to legacy parsing only if missing (defensive).
        let action = (item as any).action as string | undefined;
        let audience = (item as any).audience as string | undefined;
        const channel = (item as any).channel as string | undefined;
        if (!action || !audience) {
          // Legacy: derive from title. Supported formats:
          //   "ACTION · AUDIENCE"   (current backend)
          //   "ACTION for AUDIENCE" (legacy)
          let derivedAction = item.title;
          let derivedAudience = item.audience_ids?.join(", ") || "all";
          if (item.title.includes(" \u00b7 ")) {
            const parts = item.title.split(" \u00b7 ");
            derivedAction = parts[0] || item.title;
            derivedAudience = parts[1] || derivedAudience;
          } else if (item.title.includes(" for ")) {
            const parts = item.title.split(" for ");
            derivedAction = parts[0] || item.title;
            derivedAudience = parts[1] || derivedAudience;
          }
          action = action || derivedAction;
          audience = audience || derivedAudience;
        }
        // Clean up legacy URN prefix only when no first-class action was provided
        if (!(item as any).action) {
          action = action.replace(/^urn:scte:224:action:/, "");
        }
        return {
          id: item.id,
          title: item.title,
          starts_at: item.starts_at,
          ends_at: item.ends_at,
          status: item.status,
          audience: audience,
          action: action,
          channel: channel || row.channel_name,
          kind: item.kind || "default",
          tooltip: `${action} for ${audience}`,
          matched_policies: (item as any).matched_policies || [],
        };
      })
    }));
  } catch (error) {
    console.error("Failed to fetch EPG data:", error);
    return [];
  }
}

/**
 * createEpgItem: Insert a new mediapoint into scte224_policies.
 */
export async function createEpgItem(
  channel: string,
  audience: string,
  action: string,
  match_time: string,
  duration: string,
): Promise<{ ok: boolean; id?: number }> {
  const { data } = await api.post<{ ok: boolean; id?: number }>("/epg/create", {
    channel, audience, action, match_time, duration,
  });
  return data;
}

// ---------- Phase 0: channel-scoped slot ops (create_slot / move_slot / undo) ----------

export type SlotTrim = { action: string; audience: string; old_duration: string; new_duration: string };
export type SlotDeleted = { action: string; audience: string };
export type SlotResult = {
  ok: boolean;
  op_id: string;
  created: number;
  trimmed: SlotTrim[];
  deleted: SlotDeleted[];
};

/**
 * createSlot: channel-scoped Add MediaPoint. The backend derives the channel's
 * audience set, trims overlapping rows (5-min minimum), inserts one row per
 * audience, and returns an op_id for single-level undo.
 */
export async function createSlot(
  channel: string,
  action: string,
  match_time: string,
  duration: string,
): Promise<SlotResult> {
  const { data } = await api.post<SlotResult>("/epg/create_slot", {
    channel, action, match_time, duration,
  });
  return data;
}

export type MoveResult = { ok: boolean; op_id: string | null; moved: number };

/**
 * moveSlot: move every audience variant of a slot to a new start, as one
 * undoable op.
 */
export async function moveSlot(
  channel: string,
  action: string,
  match_time: string,
  new_match_time: string,
): Promise<MoveResult> {
  const { data } = await api.post<MoveResult>("/epg/move_slot", {
    channel, action, match_time, new_match_time,
  });
  return data;
}

/**
 * undoLastOp: reverse the last create_slot/move_slot op by its op_id.
 */
export async function undoLastOp(
  op_id: string,
): Promise<{ ok: boolean; removed: number; restored: number }> {
  const { data } = await api.post<{ ok: boolean; removed: number; restored: number }>(
    "/epg/undo",
    { op_id },
  );
  return data;
}

/**
 * updateEpgItem: Update an EPG item/MediaPoint
 */
export async function updateEpgItem(
  channel: string,
  audience: string,
  action: string,
  match_time: string,
  updates: {
    new_action?: string;
    new_audience?: string;
    new_match_time?: string;
    new_duration?: string;
  }
): Promise<{ ok: boolean }> {
  try {
    const { data } = await api.post<{ ok: boolean }>("/epg/update", {
      channel,
      audience,
      action,
      match_time,
      ...updates
    });
    return data;
  } catch (error) {
    console.error("Failed to update EPG item:", error);
    return { ok: false };
  }
}

/**
 * Get all audiences
 */
export async function getAudiences(): Promise<Audience[]> {
  try {
    const { data } = await api.get<Audience[]>("/audiences");
    return data;
  } catch (error) {
    console.error("Failed to fetch audiences:", error);
    return [];
  }
}

/**
 * Create a new audience
 */
export async function createAudience(audience: Omit<Audience, "id">): Promise<Audience | null> {
  try {
    const { data } = await api.post<Audience>("/audiences", audience);
    return data;
  } catch (error) {
    console.error("Failed to create audience:", error);
    return null;
  }
}

/**
 * Get all policies
 */
export async function getPolicies(): Promise<Policy[]> {
  try {
    const { data } = await api.get<Policy[]>("/policies");
    return data;
  } catch (error) {
    console.error("Failed to fetch policies:", error);
    return [];
  }
}

/**
 * Create a new policy
 */
export async function createPolicy(policy: Omit<Policy, "id">): Promise<Policy | null> {
  try {
    const { data } = await api.post<Policy>("/policies", policy);
    return data;
  } catch (error) {
    console.error("Failed to create policy:", error);
    return null;
  }
}

/**
 * Update an existing policy
 */
export async function updatePolicy(id: string, policy: Partial<Policy>): Promise<Policy | null> {
  try {
    const { data } = await api.patch<Policy>(`/policies/${id}`, policy);
    return data;
  } catch (error) {
    console.error("Failed to update policy:", error);
    return null;
  }
}

/**
 * Delete a policy
 */
export async function deletePolicy(id: string): Promise<boolean> {
  try {
    await api.delete(`/policies/${id}`);
    return true;
  } catch (error) {
    console.error("Failed to delete policy:", error);
    return false;
  }
}

/**
 * Get all events
 */
export async function getEvents(): Promise<EventRow[]> {
  try {
    const { data } = await api.get<EventRow[]>("/events");
    return data;
  } catch (error) {
    console.error("Failed to fetch events:", error);
    return [];
  }
}

/**
 * Get component catalog by type
 */
export async function getComponents(type: "TRIGGER" | "ACTION" | "CONSTRAINT"): Promise<CatalogItem[]> {
  try {
    const { data } = await api.get<CatalogItem[]>("/components", { params: { type } });
    return data;
  } catch (error) {
    console.error(`Failed to fetch ${type} components:`, error);
    return [];
  }
}

/**
 * Load XML files into database
 */
export async function loadXml(): Promise<any> {
  try {
    const { data } = await api.post<any>("/load_xml");
    return data;
  } catch (error) {
    console.error("Failed to load XML:", error);
    return null;
  }
}

/**
 * Health check
 */
export async function healthCheck(): Promise<boolean> {
  try {
    const { data } = await api.get<{ status: string }>("/health");
    return data.status === "ok";
  } catch (error) {
    console.error("Health check failed:", error);
    return false;
  }
}

// ---------- Auth API ----------
export type GroupRef = { id: number; name: string; role: string };
export type LoginResponse = {
  token: string;
  user: { id: number; username: string; role: string };
  groups: GroupRef[];
};
export type MeGroups = {
  uid: number;
  super_admin: boolean;
  member_of: number[];
  admin_of: number[];
  groups: GroupRef[];
};

/** Authenticate and persist the JWT. Throws on bad credentials. */
export async function login(username: string, password: string): Promise<LoginResponse> {
  const { data } = await api.post<LoginResponse>("/auth/login", { username, password });
  setToken(data.token);
  return data;
}

/** Resolve the current caller's effective groups (requires a valid token). */
export async function getMeGroups(): Promise<MeGroups> {
  const { data } = await api.get<MeGroups>("/me/groups");
  return data;
}

export function logout(): void {
  clearToken();
}

// ---------- Admin API (Phase 4/5) ----------
export type UserRow = {
  id: number; username: string; role: string; enabled: boolean;
  must_change_password: boolean; groups: GroupRef[]; created_at?: string;
};
export type GroupRow = {
  id: number; name: string; description?: string | null;
  created_at?: string; updated_at?: string;
};
export type ChannelRow = {
  id: number; name: string; display_name?: string | null; color?: string | null;
  owner_user_id?: number | null; is_global: boolean; groups: number[];
  can_write: boolean; created_at?: string;
};
export type MeResponse = {
  id: number; username: string; role: string; enabled: boolean;
  must_change_password: boolean; super_admin: boolean;
  member_of: number[]; admin_of: number[]; groups: GroupRef[];
};

export async function getMe(): Promise<MeResponse> {
  return (await api.get<MeResponse>("/me")).data;
}
export async function changeMyPassword(current_password: string, new_password: string) {
  return (await api.post<{ ok: boolean }>("/me/password", { current_password, new_password })).data;
}

// Users
export async function listUsers(): Promise<UserRow[]> {
  return (await api.get<UserRow[]>("/users")).data;
}
export async function createUser(input: { username: string; password?: string; role?: string; group_ids?: number[] }) {
  return (await api.post<{ ok: boolean; id: number; username: string; temp_password: string | null }>("/users", input)).data;
}
export async function updateUser(id: number, input: { enabled?: boolean; role?: string; reset_password?: boolean; group_ids?: number[] }) {
  return (await api.put<{ ok: boolean; temp_password: string | null }>(`/users/${id}`, input)).data;
}

// Groups
export async function listGroups(): Promise<GroupRow[]> {
  return (await api.get<GroupRow[]>("/groups")).data;
}
export async function createGroup(input: { name: string; description?: string }) {
  return (await api.post<{ ok: boolean; id: number }>("/groups", input)).data;
}
export async function updateGroup(id: number, input: { name: string; description?: string }) {
  return (await api.put<{ ok: boolean }>(`/groups/${id}`, input)).data;
}
export async function deleteGroup(id: number) {
  return (await api.delete<{ ok: boolean }>(`/groups/${id}`)).data;
}
export async function listGroupMembers(id: number): Promise<{ user_id: number; username: string; role: string; created_at?: string }[]> {
  return (await api.get<{ user_id: number; username: string; role: string }[]>(`/groups/${id}/members`)).data;
}
export async function addGroupMember(id: number, user_id: number, role: string) {
  return (await api.post<{ ok: boolean }>(`/groups/${id}/members`, { user_id, role })).data;
}
export async function removeGroupMember(id: number, user_id: number) {
  return (await api.delete<{ ok: boolean }>(`/groups/${id}/members/${user_id}`)).data;
}

// Channels
export async function listChannels(): Promise<ChannelRow[]> {
  return (await api.get<ChannelRow[]>("/channels")).data;
}
export async function createChannel(input: { name: string; display_name?: string; color?: string; is_global?: boolean; group_ids?: number[] }) {
  return (await api.post<{ ok: boolean; id: number }>("/channels", input)).data;
}
export async function updateChannel(id: number, input: { display_name?: string; color?: string; is_global?: boolean; group_ids?: number[] }) {
  return (await api.put<{ ok: boolean }>(`/channels/${id}`, input)).data;
}
export async function deleteChannel(id: number) {
  return (await api.delete<{ ok: boolean }>(`/channels/${id}`)).data;
}


// ---------- Phase I: SCTE-224 decision / status / audit ----------
export type DecisionResult = {
  source: string;
  eventtime: string;
  channelId: number;
  audience: { type: string; value: string | null };
  signal: string | null;
  decision: null | {
    mediaPoint: string; policy: string; viewingPolicy: string;
    audience: string; audienceName: string; matchTime: string; duration: string;
    trigger: string; actions: string[];
  };
  active: Array<{
    mediaPoint: string; policy: string; viewingPolicy: string; audience: string;
    actions: string[]; priority: number | null; matchTime: string; trigger: string;
  }>;
  auditId: string;
};
export async function getDecision(params: {
  source: string; eventtime: string; audiencetype?: string; audiencevalue?: string;
  segmentationupidtype?: string; signalid?: string;
}): Promise<DecisionResult> {
  return (await api.get<DecisionResult>("/decision", { params })).data;
}

export type StatusResult = {
  source: string; at: string; active: boolean;
  mediaPoints: Array<{ mediaPoint: string; policy: string; matchTime: string; duration: string }>;
  auditId: string;
};
export async function getStatus(params: { source: string; at: string }): Promise<StatusResult> {
  return (await api.get<StatusResult>("/status", { params })).data;
}

export type AuditRow = {
  id: string; parentId: string | null; trigger: string; policyMode: string | null;
  result: string; href: string | null; role: string | null; authorization: string | null;
  lastUpdated: string | null; createdAt: string; channelId: number | null; description: string | null;
};
export type AuditResults = { size: number; offset: number; limit: number; audits: AuditRow[] };
export async function getAudit(params: {
  role?: string; limit?: number; offset?: number; updatedAfter?: string; updatedBefore?: string;
} = {}): Promise<AuditResults> {
  return (await api.get<AuditResults>("/audit", { params })).data;
}


// ---------- Phase I part 2: SCTE graph resource reads (admin) ----------
export type ScteAudience = { id: string; name: string; criteria: string | null; match: string | null; parentId: string | null };
export async function getScteAudiences(): Promise<ScteAudience[]> {
  return (await api.get<ScteAudience[]>("/scte/audiences")).data;
}
export type ScteVp = { id: string; audienceId: string | null; description: string | null; actions: { namespace: string; action: string }[] };
export async function getScteVps(): Promise<ScteVp[]> {
  return (await api.get<ScteVp[]>("/scte/viewing_policies")).data;
}
export type ScteMp = { id: string; channel: string; matchTime: string | null; duration: string | null; policy: string; hasSignal: boolean; audiences: string[] };
export type ScteMpPage = { size: number; items: ScteMp[] };
export async function getScteMediapoints(params: { channel?: string; limit?: number; from?: string } = {}): Promise<ScteMpPage> {
  return (await api.get<ScteMpPage>("/scte/mediapoints", { params })).data;
}


// ---------- Phase L: SCTE graph resource writes (admin) ----------
// Audiences / ViewingPolicies are super-admin; MediaPoints are channel-write.
// POST creates, PUT updates (both upsert server-side); DELETE takes ?id=.

export type ScteAudienceInput = { id?: string; name: string; criteria?: string | null; match?: string | null; parentId?: string | null };
export async function upsertScteAudience(body: ScteAudienceInput, update = false): Promise<ScteAudience> {
  const res = update ? await api.put<ScteAudience>("/scte/audiences", body) : await api.post<ScteAudience>("/scte/audiences", body);
  return res.data;
}
export async function deleteScteAudience(id: string): Promise<void> {
  await api.delete<void>("/scte/audiences", { params: { id } });
}

export type ScteVpInput = { id?: string; audienceId?: string | null; description?: string | null; actions: { namespace: string; action: string }[] };
export async function upsertScteVp(body: ScteVpInput, update = false): Promise<ScteVp> {
  const res = update ? await api.put<ScteVp>("/scte/viewing_policies", body) : await api.post<ScteVp>("/scte/viewing_policies", body);
  return res.data;
}
export async function deleteScteVp(id: string): Promise<void> {
  await api.delete<void>("/scte/viewing_policies", { params: { id } });
}

export type ScteMpSignalInput = { match?: string; schema?: string; tolerance?: string; asserts: string[] };
export type ScteMpInput = { id?: string; channel: string; matchTime: string; duration?: string; policyId: string; priority?: number; order?: number; matchSignal?: ScteMpSignalInput };
export async function upsertScteMediapoint(body: ScteMpInput, update = false): Promise<ScteMp> {
  const res = update ? await api.put<ScteMp>("/scte/mediapoints", body) : await api.post<ScteMp>("/scte/mediapoints", body);
  return res.data;
}
export async function deleteScteMediapoint(id: string): Promise<void> {
  await api.delete<void>("/scte/mediapoints", { params: { id } });
}
